/**
 * The error contract shared by the database triggers, the API and the client (PRD §6.4).
 *
 * A rule violation is identified by its `code`, never by the message: the triggers raise
 * German text for the server log, while the UI renders `errors.<code>` through i18n. Codes
 * here must stay in step with the DETAIL payloads raised in `migrations/0003`–`0007`.
 */

export const ERROR_CODES = [
  // day records (PRD §6.4, D1–D5)
  "DAY_FUTURE_HOURS",
  "DAY_BEFORE_ASSIGNMENT",
  "DAY_IS_HOLIDAY",
  "ABSENCE_FULL_REQUIRES_ZERO",
  "ABSENCE_INCONSISTENT",
  /** Full absence blocked because another project still has tracked hours that day (Gesamt / time_entries). */
  "ABSENCE_BLOCKED_BY_OTHER_PROJECT_HOURS",
  /** Week save: a day with 0 Gesamt + 0 F&E must carry an absence reason. */
  "ZERO_HOURS_REQUIRE_ABSENCE",
  "DAILY_CAP_EXCEEDED",
  "WEEKLY_HOURS_EXCEEDED",
  // time entries (PRD §6.4, T1–T5)
  "ENTRY_FUTURE_HOURS",
  "ENTRY_BEFORE_ASSIGNMENT",
  "ENTRY_BEFORE_PROJECT_START",
  "ENTRY_AFTER_PROJECT_END",
  /** Time entry date is after this assignment's Project Leave Date. */
  "ENTRY_AFTER_LEAVE",
  /** A reported year / a locked month outside the project term — neither can hold entries. */
  "YEAR_BEFORE_PROJECT_START",
  "YEAR_AFTER_PROJECT_END",
  "MONTH_BEFORE_PROJECT_START",
  "MONTH_AFTER_PROJECT_END",
  "DAY_RECORD_MISSING",
  "RD_EXCEEDS_TOTAL",
  "CROSS_PROJECT_OVERBOOKING",
  // shared by both
  "NO_ASSIGNMENT",
  "NO_WORK_PROFILE",
  "RETRO_REASON_REQUIRED",
  /** Re-saving a week that modifies an already-persisted day requires a change reason (≥3 chars). */
  "CHANGE_REASON_REQUIRED",
  // locks & audit (PRD §7)
  "MONTH_LOCKED",
  "AUDIT_APPEND_ONLY",
  // tenancy invariants (PRD §3.3)
  "ASSIGNMENT_COMPANY_MISMATCH",
  /** Assignment project entry date must be on/after the project start. */
  "ASSIGNMENT_ENTRY_BEFORE_PROJECT_START",
  /** Assignment project entry date must be on/before the project end (when set). */
  "ASSIGNMENT_ENTRY_AFTER_PROJECT_END",
  /** Assignment project entry date must be on/before the +2 Berlin-month horizon. */
  "ASSIGNMENT_ENTRY_TOO_FAR_IN_FUTURE",
  /** Assignment Project Leave Date must be on/after the assignment entry date. */
  "ASSIGNMENT_LEAVE_BEFORE_ENTRY",
  /** Assignment Project Leave Date must be on/after the project start. */
  "ASSIGNMENT_LEAVE_BEFORE_PROJECT_START",
  /** Assignment Project Leave Date must be on/before the project end. */
  "ASSIGNMENT_LEAVE_AFTER_PROJECT_END",
  "FIRST_USER_MUST_BE_COMPANY_ADMIN",
  "LAST_COMPANY_ADMIN_PROTECTED",
  "EMAIL_IMMUTABLE",
  "EMAIL_TAKEN",
  /** Sum of active assignment rd_share for one person must stay ≤ configured max (default 95%). */
  "RD_SHARE_EXCEEDS_MAX",
  /** Single assignment rd_share outside `app_settings.rd_share_limits` percent band. */
  "RD_SHARE_OUT_OF_RANGE",
  // tenancy lifecycle (PRD §5.2, PLAN.md D-22/D-23)
  "ASSIGNMENT_HAS_ENTRIES",
  /** Setting leave_date would leave existing time_entries after it. */
  "ASSIGNMENT_HAS_ENTRIES_AFTER_LEAVE",
  "HAS_TRACKED_DATA",
  // authentication & authorization (PRD §8, PLAN.md D-21)
  "UNAUTHENTICATED",
  "INVALID_CREDENTIALS",
  "ACCOUNT_DISABLED",
  "PASSWORD_SETUP_REQUIRED",
  "WEAK_PASSWORD",
  "FORBIDDEN",
  /** Cloudflare Turnstile verification failed (unauthenticated auth POSTs). */
  "TURNSTILE_FAILED",
  "NOT_FOUND",
  "RATE_LIMITED",
  "TOKEN_INVALID",
  "TOKEN_EXPIRED",
  "TOKEN_USED",
  // generic
  "INVALID_INPUT",
  "CONFLICT",
  "CONSTRAINT_VIOLATED",
  "REFERENCE_NOT_FOUND",
  "MISSING_ACTOR_CONTEXT",
  "INTERNAL_ERROR",
  // week attachments (PLAN.md D-67)
  "ATTACHMENT_TOO_LARGE",
  "ATTACHMENT_TYPE_REJECTED",
  "ATTACHMENT_CAP_EXCEEDED",
  "ATTACHMENT_QUOTA_EXCEEDED",
  "ATTACHMENT_ZIP_TOO_LARGE",
  /** Stundenzettel export: Rn. 124 Vorhabens-ID (J5) and/or FuE-Tätigkeit (W7) blank. */
  "STUNDENZETTEL_MINDESTANGABEN_MISSING",
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === "string" && (ERROR_CODES as readonly string[]).includes(value);
}

/** One project's share of a person's day, as listed in the T4 breakdown (Σ R&D). */
export interface ProjectHours {
  project_id: string;
  project_name: string;
  rd_hours: number;
}

/**
 * Absence-blocked breakdown row (C5): toast X-hr cites shared-day `total_hours` (Gesamt),
 * not R&D ACTUAL — Gesamt may be > 0 while `rd_hours` is 0.
 */
export interface AbsenceProjectHours {
  project_id: string;
  project_name: string;
  /** Shared day Gesamt (HOURS) — toast uses this. */
  total_hours: number;
  /** Project ACTUAL R&D; may be 0 when Gesamt > 0. */
  rd_hours: number;
}

/**
 * The payload PRD §6.4 T4 requires verbatim: the client toast cites `total_hours` (Gesamt)
 * plus a per-project breakdown so the person sees which other project is consuming the day.
 * `daily_cap` remains in the payload for compatibility; it is not the T4 R&D limit (C2).
 */
export interface CrossProjectOverbookingPayload {
  code: "CROSS_PROJECT_OVERBOOKING";
  date: string;
  daily_cap: number;
  total_hours: number;
  breakdown: ProjectHours[];
  attempted: { project_id: string; rd_hours: number };
}

/**
 * Full-day absence refused because another project still has tracked hours on that day
 * (time_entries including rd=0, and/or shared Gesamt > 0). Toast X-hr uses breakdown
 * `total_hours` (Gesamt). Client formats the breakdown list like cross-project overbook.
 */
export interface AbsenceBlockedByOtherProjectHoursPayload {
  code: "ABSENCE_BLOCKED_BY_OTHER_PROJECT_HOURS";
  date: string;
  breakdown: AbsenceProjectHours[];
}

export interface MonthLockedPayload {
  code: "MONTH_LOCKED";
  year: number;
  month: number;
  project_id: string;
  /** Display name of the project holding the active lock (PRD §7.2 error copy). */
  project_name: string;
}

/** Login vs one-time-link vs attachment upload throttle (PRD §8). */
export type RateLimitTarget = "login" | "magic_link" | "attachment_upload";

export interface RateLimitedPayload {
  code: "RATE_LIMITED";
  target: RateLimitTarget;
  resetAt: string;
  seconds: number;
  attempts: number;
  window_seconds: number;
  lockout_seconds: number;
}

export interface ErrorPayload {
  code: ErrorCode;
  [key: string]: unknown;
}

export type AnyErrorPayload =
  | ErrorPayload
  | CrossProjectOverbookingPayload
  | AbsenceBlockedByOtherProjectHoursPayload
  | MonthLockedPayload
  | RateLimitedPayload;

export function isRateLimitedPayload(payload: AnyErrorPayload): payload is RateLimitedPayload {
  if (payload.code !== "RATE_LIMITED") return false;
  const rec = payload as Record<string, unknown>;
  return (
    (rec.target === "login" || rec.target === "magic_link" || rec.target === "attachment_upload") &&
    typeof rec.resetAt === "string" &&
    typeof rec.attempts === "number" &&
    typeof rec.window_seconds === "number"
  );
}

/** The JSON body every failing API request returns (PRD §12: raw DB errors never reach a client). */
export interface ApiErrorBody {
  error: AnyErrorPayload;
}

/**
 * HTTP status per code. Validation is 422, a frozen month is a conflict, the rest are bugs.
 *
 * The 401/403/404 split follows PLAN.md D-21: no session is a 401, a resource the caller may not
 * even know about is a 404, and an action they may not perform on something they can see is 403.
 */
export function statusForCode(code: ErrorCode): number {
  switch (code) {
    case "UNAUTHENTICATED":
    case "INVALID_CREDENTIALS":
      return 401;
    // `ASSIGNMENT_HAS_ENTRIES` is a 403 because only Grantonomy may still touch it (matrix #10).
    case "FORBIDDEN":
    case "TURNSTILE_FAILED":
    case "ACCOUNT_DISABLED":
    case "PASSWORD_SETUP_REQUIRED":
    case "ASSIGNMENT_HAS_ENTRIES":
      return 403;
    case "NOT_FOUND":
      return 404;
    case "RATE_LIMITED":
      return 429;
    case "TOKEN_INVALID":
    case "TOKEN_EXPIRED":
    case "TOKEN_USED":
      return 400;
    case "MONTH_LOCKED":
    case "CONFLICT":
    case "EMAIL_TAKEN":
    case "HAS_TRACKED_DATA":
      return 409;
    case "MISSING_ACTOR_CONTEXT":
    case "AUDIT_APPEND_ONLY":
    case "INTERNAL_ERROR":
      return 500;
    default:
      return 422;
  }
}
